<template>
  <v-container fluid class="pa-0">
    <v-layout row wrap>
      <!-- NO TABLE/LIST - Only Dialog -->

      <!-- Compliance Create/Edit Dialog -->
      <v-dialog v-model="showCompliance" persistent max-width="800px">
        <v-container grid-list-md class="pa-0" fluid>
          <v-flex xs12 mx-auto>
            <v-form ref="complianceForm" @submit.prevent="saveCompliance(lCompliance.action)">
              <v-card flat>
                <v-card-title class="title grey lighten-4">
                  {{ lCompliance.action }} Compliance
                </v-card-title>

                <v-card-text>
                  <v-layout row wrap>
                    <!-- Name -->
                    <v-flex xs12>
                      <v-text-field
                        v-model="lCompliance.name"
                        label="Name *"
                        :rules="[rules.required]"
                        maxlength="100"
                      ></v-text-field>
                    </v-flex>

                    <!-- Compliance Type -->
                    <v-flex xs12 sm6>
                      <v-select
                        v-model="lCompliance.compliance_type_id"
                        :items="complianceTypeList"
                        item-text="name"
                        item-value="compliance_type_id"
                        label="Compliance Type *"
                        :rules="[rules.required]"
                      ></v-select>
                    </v-flex>

                    <!-- Compliance Category -->
                    <v-flex xs12 sm6>
                      <v-select
                        v-model="lCompliance.compliance_category_id"
                        :items="complianceCategoryList"
                        item-text="name"
                        item-value="id"
                        label="Compliance Category *"
                        :rules="[rules.required]"
                        @change="onComplianceCategoryChange"
                      ></v-select>
                    </v-flex>

                    <!-- Compliance Subcategory -->
                    <v-flex xs12 sm6>
                      <v-select
                        v-model="lCompliance.compliance_subcategory_id"
                        :items="filteredComplianceSubcategories"
                        item-text="name"
                        item-value="id"
                        label="Compliance Subcategory *"
                        :rules="[rules.required]"
                        :disabled="!lCompliance.compliance_category_id"
                      ></v-select>
                    </v-flex>

                    <!-- Frequency -->
                    <v-flex xs12 sm6>
                      <v-select
                        v-model="lCompliance.frequency"
                        :items="['Yearly', 'Half-yearly', 'Quarterly', 'Monthly', 'Onetime']"
                        label="Frequency *"
                        :rules="[rules.required]"
                      ></v-select>
                    </v-flex>

                    <!-- Valid From -->
                    <v-flex xs12 sm6>
                      <v-menu
                        v-model="validFromMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                      >
                        <template v-slot:activator="{ on }">
                          <v-text-field
                            v-model="lCompliance.valid_from"
                            label="Valid From (Start/Issue Date) *"
                            readonly
                            v-on="on"
                            :rules="[rules.required]"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="lCompliance.valid_from"
                          @input="validFromMenu = false"
                        ></v-date-picker>
                      </v-menu>
                    </v-flex>

                    <!-- Valid To -->
                    <v-flex xs12 sm6>
                      <v-menu
                        v-model="validToMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                      >
                        <template v-slot:activator="{ on }">
                          <v-text-field
                            v-model="lCompliance.valid_to"
                            label="Valid To (Expiry Date) *"
                            readonly
                            v-on="on"
                            :rules="[rules.required]"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="lCompliance.valid_to"
                          @input="validToMenu = false"
                          :min="lCompliance.valid_from"
                        ></v-date-picker>
                      </v-menu>
                    </v-flex>

                    <!-- Notify Before Days -->
                    <v-flex xs12 sm6>
                      <v-text-field
                        v-model.number="lCompliance.notify_before_days"
                        label="Notify Before Days"
                        type="number"
                        min="0"
                        hint="Number of days before expiry to send notification"
                      ></v-text-field>
                    </v-flex>

                    <!-- Remarks -->
                    <v-flex xs12>
                      <v-textarea
                        v-model="lCompliance.remarks"
                        label="Remarks"
                        rows="3"
                        hint="Additional notes or comments"
                      ></v-textarea>
                    </v-flex>

                    <!-- Show on Dashboard -->
                    <v-flex xs12>
                      <v-checkbox
                        v-model="lCompliance.show_dashboard"
                        label="Show on Dashboard"
                        color="primary"
                      ></v-checkbox>
                    </v-flex>

                    <!-- File Attachments - Similar to Incident.vue -->
                    <v-flex xs12>
                      <v-subheader class="pa-0">Attachments</v-subheader>
                      <input
                        style="display:none"
                        type="file"
                        ref="fileInput"
                        accept=".xls, .xlsx, .pdf, .doc, .docx, image/png, image/gif, image/jpeg"
                        multiple
                        @change="loadFilesFromFileBrowser()"
                      />
                      <v-btn icon @click="pickFile">
                        <v-icon>attach_file</v-icon>
                      </v-btn>

                      <!-- Display attached files -->
                      <span v-if="lCompliance.files && lCompliance.files.filter(rec => rec.is_selected).length > 0">
                        <span v-for="(file, index) in lCompliance.files" :key="index">
                          <v-chip v-model="file.is_selected" close @input="$forceUpdate()">
                            {{ file.file_name }}
                            <!-- Show download link for existing files (not changed) -->
                            <span v-if="!file.is_changed">
                              <a :href="config.FM_SPACES_BASE_URL + file.file_url" download style="text-decoration: none;">
                                <v-btn small icon class="mr-0 grey lighten-1 grey--text text--lighten-4">
                                  <v-icon small>file_download</v-icon>
                                </v-btn>
                              </a>
                            </span>
                          </v-chip>
                        </span>
                      </span>
                      <span v-else>
                        Attachment if any<br />
                        <span class="caption">(supports images, pdfs, docs and excels upto 20MB)</span>
                      </span>
                    </v-flex>

                  </v-layout>
                </v-card-text>

                <v-card-actions class="pt-0">
                  <v-spacer></v-spacer>
                  <v-btn flat @click.stop="closeDialog()">Cancel</v-btn>
                  <v-btn
                    flat
                    v-if="lCompliance.action === 'Update'"
                    @click="deleteCompliance(lCompliance)"
                  >
                    Delete
                  </v-btn>
                  <v-btn
                    flat
                    type="submit"
                    color="accent"
                    :disabled="beingSaved"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-form>
          </v-flex>
        </v-container>
      </v-dialog>
    </v-layout>

    <!-- snack bar for reporting -->
    <v-snackbar :color="sbarCol" v-model="sbar" :timeout="3000" multi-line>
      <span class="mx-auto">{{ sbarMsg }}</span>
    </v-snackbar>
  </v-container>
</template>

<script>
import config from '@/config/app-cfg'
import handy from '@/utils/handy'

export default {
  data () {
    return {
      sbar: false,
      sbarCol: 'error',
      sbarMsg: '',
      beingSaved: false,
      config: config,
      handy: handy,
      rules: handy.rules,
      showCompliance: false,
      lCompliance: {
        action: 'Create',
        compliance_id: null,
        name: '',
        compliance_type_id: null,
        compliance_category_id: null,
        compliance_subcategory_id: null,
        remarks: '',
        frequency: '',
        valid_from: '',
        valid_to: '',
        show_dashboard: false,
        notify_before_days: 0,
        files: []
      },
      validFromMenu: false,
      validToMenu: false,
      complianceTypeList: [],
      complianceCategoryList: [],
      complianceSubcatList: []
    }
  },
  computed: {
    filteredComplianceSubcategories () {
      if (!this.lCompliance.compliance_category_id) {
        return []
      }
      return this.complianceSubcatList.filter(
        sub => sub.category_id === this.lCompliance.compliance_category_id
      )
    }
  },
  mounted () {
    this.loadComplianceType()
    this.loadComplianceCategory()

    // Check if we need to open dialog with a specific compliance ID from route params
    if (this.$route.params.id) {
      this.loadComplianceById(this.$route.params.id)
    }
  },
  methods: {
    msg (m, type = 0) {
      if (type === 0) {
        this.sbarCol = 'error'
      } else if (type === 1) {
        this.sbarCol = 'success'
      } else {
        this.sbarCol = 'info'
      }
      this.sbar = true
      this.sbarMsg = m
    },

    loadComplianceType () {
      this.axios.get(config.API_URL + '/getComplianceType?comm_id=' + config.COMM_ID)
      .then(response => {
        // Normalize the response to ensure consistent field naming
        this.complianceTypeList = response.data.map(item => ({
          ...item,
          compliance_type_id: item.id || item.compliance_type_id,
          id: item.id || item.compliance_type_id
        }))
      })
      .catch(() => {
        this.msg('Error loading compliance types', 0)
      })
    },

    loadComplianceCategory () {
      this.axios.get(config.API_URL + '/getComplianceCategory?comm_id=' + config.COMM_ID)
      .then(response => {
        this.complianceCategoryList = response.data
        // Load subcategories after categories are loaded
        this.loadComplianceSubcategory()
      })
      .catch(() => {
        this.msg('Error loading compliance categories', 0)
      })
    },

    loadComplianceSubcategory () {
      this.axios.get(config.API_URL + '/getComplianceSubcategory?comm_id=' + config.COMM_ID)
      .then(response => {
        this.complianceSubcatList = response.data
      })
      .catch(() => {
        this.msg('Error loading compliance subcategories', 0)
      })
    },

    loadComplianceById (complianceId) {
      // Load compliance data and open dialog for editing
      this.axios.get(config.API_URL + '/getCompliance?id=' + complianceId)
      .then(response => {
        if (response.data && response.data.length > 0) {
          this.prepareEditCompliance(response.data[0])
        }
      })
      .catch(() => {
        this.msg('Error loading compliance record', 0)
      })
    },

    loadComplianceAttachment (complianceId) {
      var path = require('path')
      var qry = {
        params: {
          compliance_id: complianceId
        }
      }
      this.axios.get(config.API_URL + '/getComplianceAttachments/', qry)
      .then(response => {
        this.lCompliance.files = []
        response.data.forEach((rec) => {
          this.lCompliance.files.push({
            file_url: rec.file_url,
            file_name: path.basename(rec.file_url),
            is_selected: true,
            is_changed: false
          })
        })
      })
      .catch(err => {
        console.error('Error loading attachments:', err)
        this.lCompliance.files = []
      })
    },

    prepareEditCompliance (item) {
      const formatDateForPicker = (isoStr) => {
        if (!isoStr) return ''
        const d = new Date(isoStr)
        const month = (d.getMonth() + 1).toString().padStart(2, '0')
        const day = d.getDate().toString().padStart(2, '0')
        return `${d.getFullYear()}-${month}-${day}`
      }

      // Ensure compliance_type_id is properly set
      const typeId = item.compliance_type_id || item.type_id

      this.lCompliance = {
        action: 'Update',
        id: item.id,
        name: item.compliance_name || item.name,
        compliance_type_id: typeId,
        compliance_category_id: item.category_id || item.compliance_category_id,
        compliance_subcategory_id: item.subcategory_id || item.compliance_subcategory_id,
        remarks: item.remarks || '',
        frequency: item.frequency || '',
        valid_from: formatDateForPicker(item.valid_from),
        valid_to: formatDateForPicker(item.valid_to),
        show_dashboard: item.show_dashboard || false,
        notify_before_days: item.notify_before_days || 0,
        files: []
      }

      // Load attachments for this compliance
      if (item.id) {
        this.loadComplianceAttachment(item.id)
      }

      this.showCompliance = true
    },

    onComplianceCategoryChange () {
      // Reset subcategory when category changes
      this.lCompliance.compliance_subcategory_id = null
    },

    pickFile () {
      this.$refs.fileInput.click()
    },

    loadFilesFromFileBrowser () {
      const files = event.target.files
      Array.from(files).forEach((file) => {
        const fileReader = new FileReader()
        fileReader.onload = e => {
          this.lCompliance.files.push({
            file_url: e.target.result.split(',')[1],
            file_name: file.name,
            is_selected: true,
            is_changed: true
          })
          this.$forceUpdate()
        }
        fileReader.readAsDataURL(file)
      })
      // Reset the input
      this.$refs.fileInput.value = ''
    },

    closeDialog () {
      this.showCompliance = false
      // Optionally navigate back or emit event
      if (this.$route.params.id) {
        this.$router.go(-1)
      }
    },

    saveCompliance (action) {
      if (!this.$refs.complianceForm.validate()) {
        return
      }

      // Additional validation
      if (!this.lCompliance.name || this.lCompliance.name.trim() === '') {
        this.msg('Please enter compliance name', 0)
        return
      }

      // Critical validation for compliance_type_id
      if (!this.lCompliance.compliance_type_id || this.lCompliance.compliance_type_id === null) {
        console.error('Compliance Type ID is null or undefined:', this.lCompliance.compliance_type_id)
        this.msg('Please select compliance type', 0)
        return
      }

      if (!this.lCompliance.compliance_category_id) {
        this.msg('Please select compliance category', 0)
        return
      }
      if (!this.lCompliance.compliance_subcategory_id) {
        this.msg('Please select compliance subcategory', 0)
        return
      }
      if (!this.lCompliance.frequency) {
        this.msg('Please select frequency', 0)
        return
      }
      if (!this.lCompliance.valid_from) {
        this.msg('Please select valid from date', 0)
        return
      }
      if (!this.lCompliance.valid_to) {
        this.msg('Please select valid to date', 0)
        return
      }

      // Final sanity check before submitting
      const typeId = parseInt(this.lCompliance.compliance_type_id)
      if (isNaN(typeId) || typeId <= 0) {
        console.error('Invalid compliance_type_id after parsing:', typeId)
        this.msg('Invalid compliance type selected. Please try again.', 0)
        return
      }

      this.beingSaved = true

      const endpoint = action === 'Create' ? '/compliance/create' : '/compliance/update'

      const formatDateForMySQL = (dateStr) => {
        if (!dateStr) return null
        const d = new Date(dateStr)
        const month = (d.getMonth() + 1).toString().padStart(2, '0')
        const day = d.getDate().toString().padStart(2, '0')
        return `${d.getFullYear()}-${month}-${day}`
      }

      const payload = {
        compliance_name: this.lCompliance.name,
        compliance_type_id: parseInt(this.lCompliance.compliance_type_id),
        category_id: parseInt(this.lCompliance.compliance_category_id),
        subcategory_id: parseInt(this.lCompliance.compliance_subcategory_id),
        community_id: parseInt(config.COMM_ID),
        status: 'Active',
        valid_from: formatDateForMySQL(this.lCompliance.valid_from),
        valid_to: formatDateForMySQL(this.lCompliance.valid_to),
        remarks: this.lCompliance.remarks || '',
        frequency: this.lCompliance.frequency,
        show_dashboard: this.lCompliance.show_dashboard ? 1 : 0,
        notify_before_days: this.lCompliance.notify_before_days || 0,
        created_by: parseInt(config.USER_ID),
        updated_by: parseInt(config.USER_ID)
      }

      if (action === 'Update') {
        payload.id = parseInt(this.lCompliance.id)
      }

      // Include files in payload
      if (this.lCompliance.files && this.lCompliance.files.length > 0) {
        payload.files = this.lCompliance.files.filter(f => f.is_selected).map(f => ({
          file_url: f.file_url,
          file_name: f.file_name,
          is_selected: f.is_selected,
          is_changed: f.is_changed
        }))
      }

      this.axios.post(config.API_URL + endpoint, payload)
      .then(response => {
        if (action === 'Create') {
          this.msg('Compliance created successfully', 1)
        } else {
          this.msg('Compliance updated successfully', 1)
        }
        this.beingSaved = false
        this.closeDialog()
      })
      .catch(err => {
        this.msg(err.response && err.response.data ? err.response.data : 'Error saving compliance', 0)
        this.beingSaved = false
      })
    },

    deleteCompliance (item) {
      if (!confirm('Are you sure you want to delete this compliance?')) {
        return
      }

      this.beingSaved = true

      if (!item.id) {
        this.msg('Invalid compliance selected', 0)
        this.beingSaved = false
        return
      }

      const payload = {
        id: item.id
      }

      this.axios.post(config.API_URL + '/compliance/delete', payload)
      .then(response => {
        this.msg('Compliance deleted successfully', 1)
        this.beingSaved = false
        this.closeDialog()
      })
      .catch(err => {
        this.msg(err.response && err.response.data ? err.response.data : 'Error deleting compliance', 0)
        this.beingSaved = false
      })
    }
  }
}
</script>

<style scoped>
/* Add any custom styles here */
</style>



// WEBPACK FOOTER //
// Compliance.vue?94d08dfa